import React, { useEffect, useState } from "react";
import {
  Ambulance as AmbulanceIconLucide,
  User,
  X,
  Send,
  Info,
} from "lucide-react";
import { Form, Input, Select, DatePicker, ConfigProvider } from "antd";
import moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";
import { getHospitalDetails } from "../../utils/hospitalStorage";

const COMMAND_CENTER_URL = process.env.REACT_APP_API_COMMAND_CENTER_URL;

const formItemClass = "mb-0 ambulance-form-item";

const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 pt-2 pb-3 border-b border-gray-100 mb-4">
    <span className="w-8 h-8 rounded-lg bg-[#E8F6F4] flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#0D6B6E]" />
    </span>
    <h3 className="text-sm font-bold text-gray-800 mb-0 uppercase tracking-wide">
      {title}
    </h3>
  </div>
);

export default function Ambulance({
  isOpen,
  onClose,
  patientId,
  appointmentId = "",
  doctorName = "",
}) {
  const API_URL = process.env.REACT_APP_API_URL;
  const [form] = Form.useForm();
  const [saveLoading, setSaveLoading] = useState(false);
  const [hospitalDetails, setHospitalDetails] = useState(null);
  const [patientSnapshot, setPatientSnapshot] = useState(null);
  const hospitalId = localStorage.getItem("hospitalid") || "";

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setHospitalDetails(null);
      setPatientSnapshot(null);
      return;
    }
    setHospitalDetails(getHospitalDetails());
    if (patientId) {
      fetchPatient();
    }
  }, [isOpen, patientId]);

  const fetchPatient = async () => {
    const token = localStorage.getItem("auth_token");
    const tenantId = localStorage.getItem("tenantId");
  
    try {
      const response = await axios.get(
        `${API_URL}patient/search?patientId=${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            tenantId,
          },
        },
      );
      const patient = response?.data.data?.[0];
      if (!patient) return;

      const patientName = [patient.firstName, patient.lastName]
        .filter(Boolean)
        .join(" ");

      setPatientSnapshot({
        name: patientName,
        dob: patient.dob,
        gender: patient.gender,
        mobileNumber: patient.mobileNumber,
      });

      form.setFieldsValue({
        patientId: patient.patientId || patientId,
        patientName,
        dob: patient.dob ? moment(patient.dob) : null,
        gender: patient.gender?.toLowerCase() || undefined,
        mobileNumber: patient.mobileNumber,
        email: patient.email || "",
        escallationNumber: patient.escallationNumber || "",
        address: patient.address || "",
        pincode: patient.pinCode || patient.pincode || "",
        serviceDetailsMessage: "",
      });
    } catch (error) {
      console.error("Error fetching patient:", error);
      toast.error("Failed to load patient details.");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaveLoading(true);

      const hospital = hospitalDetails ?? getHospitalDetails();
      if (!hospital) {
        toast.error("Hospital details not found. Please log in again.");
        setSaveLoading(false);
        return;
      }
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");
      const clinicId = localStorage.getItem("clinicId");
      const clinicname = localStorage.getItem("clinicname");
      const patientAge = values.dob
        ? moment().diff(moment(values.dob), "years")
        : null;

      const ticketPayload = {
        patientDetails: {
          patientId: values.patientId || patientId || "",
          medicaseId: "",
          patientName: values.patientName?.trim() || "",
          dob: values.dob ? moment(values.dob).format("YYYY-MM-DD") : "",
          gender: values.gender || "",
          mobileNumber: String(values.mobileNumber || ""),
          email: values.email?.trim() || "",
          escallationNumber: values.escallationNumber?.trim() || "",
          address: values.address?.trim() || "",
          pincode: String(values.pincode || ""),
        },
        serviceDetailsMessage: values.serviceDetailsMessage?.trim() || "",
        tickeType: "AMBULANCE",
        ticketSource: "HOSPITAL360",
        hospitalId: hospitalId,
      };

      const ticketRes = await fetch(
        `${COMMAND_CENTER_URL}patient/add-ticket-d2c`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ticketPayload),
        },
      );

      const ticketData = await ticketRes.json();

      if (!(ticketRes.ok && ticketData?.status === 200)) {
        toast.error(
          ticketData?.message ||
            "Failed to submit ambulance enquiry. Please try again.",
        );
        return;
      }

      const ticketInfo = ticketData.data || {};

      const orderPayload = {
        clinicId,
        patientName: values.patientName?.trim() || "",
        orderPlacedTo: "SMART_CLINIC",
        patientMobileNo: parseInt(String(values.mobileNumber || "0"), 10) || 0,

        patientId: values.patientId || patientId || "",
        address: values.address?.trim() || "",
        patientAge,
        ambulaceRequestReason:ticketInfo.serviceDetailsMessage,
        amount: 0,
        discountedAmount: 0,
        enquiryTickets: [
          {
            ticketId: ticketInfo.id,
            ticketStatus: ticketInfo.status,
            createdDate: ticketInfo.createdDate,
            modifiedTime: ticketInfo.updatedDate,

          },
        ],
        paymentStatus: "COMPLETED",
        serviceType:"AMBULANCE",
      };

      const orderRes = await fetch(`${API_URL}orders/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          tenantId,
        },
        body: JSON.stringify(orderPayload),
      });

      const orderResult = await orderRes.json();

      if (orderResult.status === 200) {
        toast.success(
          orderResult.message || "Ambulance enquiry submitted successfully.",
        );
        onClose();
      } else {
        toast.error(
          orderResult?.message ||
            "Failed to submit ambulance enquiry. Please try again.",
        );
      }
    } catch (error) {
      if (error?.errorFields) return;
      console.error("Ambulance enquiry error:", error);
      toast.error("Something went wrong while submitting the enquiry.");
    } finally {
      setSaveLoading(false);
    }
  };

  if (!isOpen) return null;

  const patientName = patientSnapshot?.name || "";
  const patientAge = patientSnapshot?.dob
    ? moment().diff(moment(patientSnapshot.dob), "years")
    : null;
  const patientInitials = patientName
    ? patientName
        .split(" ")
        .filter(Boolean)
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "—";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
        <div className="bg-gradient-to-r from-[#004172] to-[#3cba84] px-5 py-4 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <AmbulanceIconLucide className="w-6 h-6 text-white" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-white mb-0">Book Ambulance</h2>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-white text-[11px] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300" />
                  24/7
                </span>
              </div>
              <p className="text-white/85 text-sm mb-0 mt-0.5">
                Submit ambulance request for patient
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg border border-white/40 flex items-center justify-center hover:bg-white/10 flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div
          className="px-5 py-3 flex flex-wrap items-center gap-4 flex-shrink-0 border-b border-[#C5E8E3]"
          style={{
            background: "linear-gradient(90deg, #E8F6F4 0%, #FFFFFF 100%)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-full bg-[#0D6B6E] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
              {patientInitials}
            </span>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 mb-0 text-sm truncate">
                {patientName || "—"}
              </p>
              <p className="text-xs text-gray-500 mb-0">
                {[
                  patientAge != null ? `Age: ${patientAge}` : null,
                  patientSnapshot?.gender,
                  patientSnapshot?.mobileNumber,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs ml-auto">
            {appointmentId && (
              <span className="px-2.5 py-1 rounded-md bg-white border border-gray-200 text-gray-600 font-medium">
                Appt #{appointmentId}
              </span>
            )}
            {doctorName && (
              <span className="px-2.5 py-1 rounded-md bg-white border border-gray-200 text-gray-600 font-medium">
                Dr. {doctorName}
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-5 py-5">
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: "#0D6B6E",
                borderRadius: 8,
                controlHeight: 40,
              },
            }}
          >
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              requiredMark={(label, { required }) =>
                required ? (
                  <>
                    {label} <span className="text-red-500">*</span>
                  </>
                ) : (
                  label
                )
              }
              className="ambulance-enquiry-form"
            >
              <Form.Item name="patientId" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="escallationNumber" hidden>
                <Input />
              </Form.Item>

              <SectionTitle icon={User} title="Patient details" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mb-6">
                <Form.Item
                  name="patientName"
                  label="Patient name"
                  rules={[{ required: true, message: "Required" }]}
                  className={formItemClass}
                >
                  <Input placeholder="Full name" />
                </Form.Item>

                <Form.Item
                  name="dob"
                  label="Date of birth"
                  rules={[{ required: true, message: "Required" }]}
                  className={formItemClass}
                >
                  <DatePicker
                    className="w-full"
                    format="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                  />
                </Form.Item>

                <Form.Item
                  name="gender"
                  label="Gender"
                  rules={[{ required: true, message: "Required" }]}
                  className={formItemClass}
                >
                  <Select
                    placeholder="Select gender"
                    options={[
                      { value: "male", label: "Male" },
                      { value: "female", label: "Female" },
                      { value: "other", label: "Other" },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  name="mobileNumber"
                  label="Mobile"
                  rules={[
                    { required: true, message: "Required" },
                    { pattern: /^\d{10}$/, message: "10 digits required" },
                  ]}
                  className={formItemClass}
                >
                  <Input maxLength={10} placeholder="10-digit mobile" />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="Email"
                  className={formItemClass}
                >
                  <Input type="email" placeholder="patient@email.com" />
                </Form.Item>

                <Form.Item
                  name="pincode"
                  label="Pin code"
                  rules={[
                    { required: true, message: "Required" },
                    { pattern: /^\d{6}$/, message: "6 digits" },
                  ]}
                  className={formItemClass}
                >
                  <Input maxLength={6} placeholder="6-digit pin code" />
                </Form.Item>
              </div>

              <SectionTitle icon={AmbulanceIconLucide} title="Ambulance request" />
              <div className="grid grid-cols-1 gap-y-1 mb-2">
                {/* <Form.Item
                  name="address"
                  label="Pickup address"
                  rules={[{ required: true, message: "Required" }]}
                  className={formItemClass}
                >
                  <Input.TextArea
                    rows={3}
                    placeholder="Enter full pickup address including landmark..."
                  />
                </Form.Item> */}

 <Form.Item
                 name="serviceDetailsMessage"
                 label="Request notes"
                 className={formItemClass}
                 rules={[
                   {
                     required: true,
                     message: "Please enter request notes",
                   },
                 ]}
               >
                 <Input.TextArea
                   rows={3}
                   placeholder="Emergency level, destination, preferred time, ambulance type, or other instructions..."
                 />
               </Form.Item>
              </div>
            </Form>
          </ConfigProvider>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0 bg-white">
          <p className="text-xs text-gray-500 mb-0 flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            Fields marked * are required
          </p>
          <div className="flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saveLoading}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#004172] to-[#3cba84] text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <AmbulanceIconLucide className="w-4 h-4" />
                  Submit Enquiry
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
